/**
 * OAuth Service for Google and Microsoft Authentication via AWS Cognito Federation
 *
 * Required environment variables:
 * - VITE_COGNITO_DOMAIN      (e.g. cbn-auth.auth.us-east-1.amazoncognito.com)
 * - VITE_COGNITO_CLIENT_ID   (Cognito App Client ID with Google/Microsoft IdPs enabled)
 *
 * Optional (only needed for direct OAuth without Cognito):
 * - VITE_GOOGLE_CLIENT_ID
 * - VITE_MICROSOFT_CLIENT_ID
 */

const COGNITO_DOMAIN = (import.meta.env.VITE_COGNITO_DOMAIN || '').trim();
const COGNITO_CLIENT_ID = (import.meta.env.VITE_COGNITO_CLIENT_ID || '').trim();
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

const buildCognitoAuthorizeUrl = (identityProvider) => {
  if (!COGNITO_DOMAIN || !COGNITO_CLIENT_ID) return null;
  const params = new URLSearchParams({
    identity_provider: identityProvider,
    client_id: COGNITO_CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
  });
  return `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
};

/**
 * Initiate Google OAuth via Cognito federation
 */
export const initiateGoogleOAuth = () => {
  const url = buildCognitoAuthorizeUrl('Google');
  if (!url) {
    alert('Google Sign-In is not yet configured. Please follow the OAuth setup guide to enable it.');
    return;
  }
  window.location.href = url;
};

/**
 * Initiate Microsoft OAuth via Cognito federation
 */
export const initiateMicrosoftOAuth = () => {
  const url = buildCognitoAuthorizeUrl('Microsoft');
  if (!url) {
    alert('Microsoft Sign-In is not yet configured. Please follow the OAuth setup guide to enable it.');
    return;
  }
  window.location.href = url;
};

/**
 * Exchange authorization code for tokens via Cognito token endpoint
 * @param {string} code - Authorization code from Cognito redirect
 * @returns {Promise<{idToken: string, accessToken: string, refreshToken: string}>}
 */
export const exchangeCodeForTokens = async (code) => {
  if (!COGNITO_DOMAIN || !COGNITO_CLIENT_ID) {
    throw new Error('Cognito OAuth not configured');
  }

  const tokenUrl = `https://${COGNITO_DOMAIN}/oauth2/token`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: COGNITO_CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
  });

  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const errText = await res.text();
    console.error('Token exchange failed:', errText);
    throw new Error('Failed to exchange authorization code for tokens');
  }

  const data = await res.json();
  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  };
};

/**
 * Parse a JWT to extract user info (without verification -- verification happens server-side)
 */
export const parseIdToken = (idToken) => {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return {
      email: payload.email || '',
      name: payload.name || payload['cognito:username'] || '',
      sub: payload.sub || '',
      provider: payload['cognito:groups']?.[0] || payload['custom:provider'] || 'federated',
    };
  } catch {
    return null;
  }
};

/**
 * Check if Cognito OAuth federation is configured
 */
export const isOAuthConfigured = () => {
  return !!(COGNITO_DOMAIN && COGNITO_CLIENT_ID);
};
